import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";

import { StyleHooks } from "@/src/hook";
import { FrontendTrackingService } from "@/src/service";
import { ComponentType, StrapiComponentType } from "@/src/type";

import { SectionModule, TextBreakpointModule } from "@/src/component/partial/__module__";
import NestedCarouselItem from "./NestedCarouselItem/NestedCarouselItem";

import styles from "./NestedCarousel.module.scss";
import { SwipeTracking } from "@/src/component/partial/SwipeTracking";

interface NestedCarouselProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.NestedCarousel> {}

function NestedCarousel({
  lazy,
  section,
  slug,
  snapToGrid,
  bottomDrawerHeight,
  classes,
  searchParams,
  mobileHint,
  deviceWidthHint,
  quality,
  sectionTypePath,
}: NestedCarouselProps) {
  const isMobile = StyleHooks.useBreakPointDown("md");
  const nestedCarousel: StrapiComponentType.Section.NestedCarousel = section.type?.[0];
  const id = `NestedCarousel-${nestedCarousel.id}`;

  const getChildItems = (lookup: string = "") => {
    return (nestedCarousel.childCarouselItems ?? []).filter((opt) => opt.itemReferenceName === lookup);
  };

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
        styledContainer: styles.carouselContainer,
      }}
      stylePrefix="NestedCarousel"
      lazy={lazy}
      styleContainerId={nestedCarousel.id}
      snapToGrid={snapToGrid}
      bottomDrawerHeight={bottomDrawerHeight}
      searchParams={searchParams}
      mobileHint={mobileHint}
      deviceWidthHint={deviceWidthHint}
      quality={quality}
    >
      <SwipeTracking
        selector={`#swiper_${id}`}
        onSwipe={() => {
          FrontendTrackingService.postTrackingLog({
            action: "swiped",
            actionId: `NestedCarousel:${nestedCarousel.id}`,
            actionIdType: "componentName:componentId",
            actionMessage: `NestedCarousel section swiped with ${section.name}`,
            triggeredFrom: "NestedCarousel.tsx:(SwipeTracking.tsx(onSwipe))",
          });
        }}
      />
      <TextBreakpointModule
        id={`NestedCarousel_title_${nestedCarousel.id}`}
        text={section.title}
        className={styles.carouselTitle}
        sectionTypePath={sectionTypePath}
      />
      <Swiper
        id={`swiper_${id}`}
        //key={isMobile ? "mobile" : "desktop"}
        breakpoints={{
          0: {
            slidesPerView: 1.1,
          },
          767: {
            slidesPerView: 2,
          },
          1025: {
            slidesPerView: 4,
          },
        }}
        className={`${styles.carouselSwiper}`}
        direction="horizontal"
        modules={[Navigation, Pagination]}
        spaceBetween={0}
        centerInsufficientSlides={true}
        centeredSlides={true}
        centeredSlidesBounds={true}
        loop={isMobile}
      >
        {(nestedCarousel?.carouselItems ?? []).map((item) => (
          <SwiperSlide
            className={styles.carouselSlide}
            key={item.id}
          >
            <NestedCarouselItem
              key={item.id}
              item={item}
              childItems={getChildItems(item.itemReferenceName)}
              lazy={lazy}
              quality={quality}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </SectionModule>
  );
}

export default NestedCarousel;
